/**
 * URL segments for the protocol wizard (order matches WIZARD_STEPS indices).
 * Generate step is exposed as "execute" in the path.
 */
export const WIZARD_STEP_SEGMENTS = ['req', 'plan', 'execute', 'validate'] as const;
export type WizardStepSegment = (typeof WIZARD_STEP_SEGMENTS)[number];

export function isWizardStepSegment(s: string): s is WizardStepSegment {
  return (WIZARD_STEP_SEGMENTS as readonly string[]).includes(s);
}

export function wizardStepIndexFromSegment(segment: string): number | null {
  const i = (WIZARD_STEP_SEGMENTS as readonly string[]).indexOf(segment);
  return i >= 0 ? i : null;
}

export function wizardSegmentFromStepIndex(index: number): WizardStepSegment {
  if (index < 0 || index >= WIZARD_STEP_SEGMENTS.length) return 'req';
  return WIZARD_STEP_SEGMENTS[index];
}

/**
 * Parses the path after `/wizard/:trialId/:version/:platform/`.
 * One segment: either a step slug (no session in URL) or a session id (default step req).
 * Two segments: session id + step slug.
 */
export function parseWizardTrailingPath(trailing: string): {
  sessionId: string | null;
  stepSegment: WizardStepSegment;
} {
  const raw = trailing.replace(/^\/+|\/+$/g, '');
  if (!raw) {
    return {sessionId: null, stepSegment: 'req'};
  }
  const parts = raw.split('/').filter(Boolean).map(p => decodeURIComponent(p));
  if (parts.length === 1) {
    const a = parts[0];
    if (isWizardStepSegment(a)) {
      return {sessionId: null, stepSegment: a};
    }
    return {sessionId: a, stepSegment: 'req'};
  }
  const step = parts[parts.length - 1];
  const sessionId = parts.slice(0, -1).join('/');
  if (isWizardStepSegment(step)) {
    return {sessionId: sessionId || null, stepSegment: step};
  }
  return {sessionId: parts[0] ?? null, stepSegment: 'req'};
}

export function buildWizardPath(
  trialId: string,
  version: string,
  platform: string,
  sessionId: string | null | undefined,
  stepIndex: number
): string {
  const seg = wizardSegmentFromStepIndex(stepIndex);
  let path = `/wizard/${trialId}/${version}/${platform}`;
  if (sessionId) {
    path += `/${encodeURIComponent(sessionId)}`;
  }
  path += `/${seg}`;
  return path;
}

export function getWizardTrailingPathname(pathname: string, basePath: string): string {
  if (pathname === basePath || pathname === `${basePath}/`) return '';
  if (pathname.startsWith(`${basePath}/`)) {
    return pathname.slice(basePath.length + 1);
  }
  return '';
}
